import { getLocalStorage } from '@runtime/shared/utils/storage'
import type { FullGameRecord } from './userModel'

export type OwnedFullGameRef = { version: 2; ownerKey: string; id: string }
export type FullGameStorageIssue =
  | 'unavailable'
  | 'write'
  | 'corrupt'
  | 'conflict'
export class FullGameStorageError extends Error {
  constructor(readonly kind: FullGameStorageIssue) {
    super('Full game storage: ' + kind)
  }
}
export function fullGameOwnerKey(userId: string | null) {
  return userId === null ? 'device' : 'account:' + userId
}
const keyFor = (ownerKey: string) =>
  'us_full_games_owner_v2:' + encodeURIComponent(ownerKey)
function object(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}
export function validOwnedFullGameRef(
  value: unknown,
): value is OwnedFullGameRef {
  return (
    object(value) &&
    value.version === 2 &&
    typeof value.ownerKey === 'string' &&
    (value.ownerKey === 'device' ||
      (value.ownerKey.startsWith('account:') && value.ownerKey.length > 8)) &&
    typeof value.id === 'string' &&
    value.id.length > 0
  )
}
export function loadOwnedFullGames(
  ownerKey: string,
): Record<string, FullGameRecord> {
  const storage = getLocalStorage()
  if (!storage) throw new FullGameStorageError('unavailable')
  let text: string | null
  try {
    text = storage.getItem(keyFor(ownerKey))
  } catch {
    throw new FullGameStorageError('unavailable')
  }
  if (text === null) return {}
  if (text.length > 10_000_000) throw new FullGameStorageError('corrupt')
  let parsed: unknown
  try {
    parsed = JSON.parse(text) as unknown
  } catch {
    throw new FullGameStorageError('corrupt')
  }
  if (
    !object(parsed) ||
    parsed.schemaVersion !== 2 ||
    parsed.ownerKey !== ownerKey ||
    !object(parsed.records)
  )
    throw new FullGameStorageError('corrupt')
  for (const [id, record] of Object.entries(parsed.records))
    if (!object(record) || record.id !== id)
      throw new FullGameStorageError('corrupt')
  return parsed.records as Record<string, FullGameRecord>
}
export function loadOwnedFullGame(
  reference: OwnedFullGameRef,
  expectedOwnerKey: string,
) {
  if (
    !validOwnedFullGameRef(reference) ||
    reference.ownerKey !== expectedOwnerKey
  )
    return null
  return loadOwnedFullGames(expectedOwnerKey)[reference.id] ?? null
}
export function saveOwnedFullGame(
  ownerKey: string,
  full: FullGameRecord,
): OwnedFullGameRef {
  if (!full.id) throw new FullGameStorageError('corrupt')
  const previous = loadOwnedFullGames(ownerKey)
  // A result identity is immutable. A distinct owner has a distinct physical key.
  if (
    previous[full.id] &&
    JSON.stringify(previous[full.id]) !== JSON.stringify(full)
  )
    throw new FullGameStorageError('conflict')
  const ordered = Object.values({ ...previous, [full.id]: full })
    .sort((A, B) => (B.timestamp ?? 0) - (A.timestamp ?? 0))
    .slice(0, 50)
  const records = Object.fromEntries(
    ordered.map((record) => [record.id, record]),
  )
  const storage = getLocalStorage()
  if (!storage) throw new FullGameStorageError('unavailable')
  try {
    storage.setItem(
      keyFor(ownerKey),
      JSON.stringify({ schemaVersion: 2, ownerKey, records }),
    )
  } catch {
    throw new FullGameStorageError('write')
  }
  return { version: 2, ownerKey, id: full.id }
}
